import React from "react";
import { Box, Button, Chip, IconButton, MenuItem, Tab, Tabs, TextField, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { enqueueSnackbar } from "notistack";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { FormDialog, PageContainer, SectionHeader } from "../../Layout/Common";
import { subscribePoll } from "../../Layout/Poller";
import JobPanel from "./JobPanel";
import StringsTab from "./StringsTab";
import CoverageTab from "./CoverageTab";
import ContextTab from "./ContextTab";
import ImportExportTab from "./ImportExportTab";
import { FIXED_TABLE, isActive } from "./loc";
import { DataTable } from "../../Layout/Common";

// The texts of a title on the backend (the Localization service): every tag with its languages and tiers, the AI
// jobs that translate and review them, the project context the AI reads, and the xlsx round trip.
// URL: /<game>/localization[/<tab>[/<folder path>]]; tabs strings | coverage | context | import | jobs.
// Texts per branch (2026-11-01): a title with per-branch tables (tree.perBranch) shows a branch selector shared by every tab
// (default Development, remembered per viewer and title); every call carries {branch} in its body, so the tabs read, edit,
// translate, review, import and export that branch's texts only. Publish writes the selected branch's own table; "Copy to
// branch…" promotes tags into another branch with a preview of the diff. A title with one shared table shows no selector.
const TABS = [["strings", "Strings"], ["coverage", "Coverage"], ["context", "Project context"], ["import", "Import / Export"], ["jobs", "Jobs"]];
const BRANCHES = ["Development", "Internal", "Beta", "Production"];
const loc = api.group("localization");
const branchKey = () => `localization.branch.${globals.selectedTitle ?? ""}`;
function rememberedBranch() {
    try { const b = localStorage.getItem(branchKey()); return BRANCHES.includes(b) ? b : "Development"; } catch { return "Development"; }
}

export default class LocalizationPage extends React.Component {
    state = { tab: "strings", tree: null, project: null, jobs: null, loading: false, deepFolder: null, deepTag: null, branch: rememberedBranch(),
        // "Publish to a version": the per-language files a client version downloads (`localization publish <branch> <M.m>`)
        publishOpen: false, publishVersion: "", publishing: false, published: null, changes: null, changesLoading: false,
        // "Copy to branch…": `localization promote <from> <to>` (preview = dry run)
        copyOpen: false, copyTo: "Internal", copyMode: "changed", copyPaths: "", copyPreview: null, copying: false };
    lastJobsPoll = 0;

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const tab = TABS.some(([k]) => k === deep[0]) ? deep[0] : "strings";
        // the rest of the path is the folder (nested folders carry slashes: news/items/Production/<key>); a news item's folder
        // lives in its own branch's texts, so its deep link selects that branch
        const deepFolder = deep.length > 1 ? deep.slice(1).join("/") : null;
        const news = /^news\/items\/(Development|Internal|Beta|Production)(\/|$)/.exec(deepFolder ?? "");
        this.setState({ tab, deepFolder, deepTag: null, ...(news ? { branch: news[1] } : {}) }, () => { this.load(); this.loadJobs(true); });
        this.unsub = subscribePoll(this.pollJobs, 5000);
    }

    // every call names the selected branch (the backend takes it out of the body; a title with one table ignores it)
    loc = (description, parts, options = {}) => {
        const payload = options.payload;
        if (payload && (typeof payload !== "object" || Array.isArray(payload) || (typeof File !== "undefined" && payload instanceof File))) return loc(description, parts, options);
        return loc(description, parts, { ...options, payload: { ...(payload ?? {}), branch: this.state.branch } });
    }

    get perBranch() { return !!this.state.tree?.perBranch; }

    setBranch = (branch) => {
        if (branch === this.state.branch) return;
        try { localStorage.setItem(branchKey(), branch); } catch { /* private window: not remembered */ }
        this.setState({ branch, tree: null, deepTag: null, copyTo: BRANCHES.find(b => b !== branch) ?? "Internal" }, () => this.load());
    }

    componentWillUnmount() { if (this.unsub) this.unsub(); }

    get canEdit() {
        if (globals.loginData?.titles?.isMaster) return true;
        const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
        const roles = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
        return roles.includes("localization");
    }

    load = () => {
        this.setState({ loading: true });
        return Promise.all([
            this.loc("Loading the tree", ["tree"], { silent: true }),
            this.loc("Loading the project", ["project"], { silent: true }),
        ]).then(([tree, project]) => {
            this.setState({ tree: typeof tree === "string" ? null : tree, project: typeof project === "string" ? null : project });
        }).catch(() => {}).finally(() => this.setState({ loading: false }));
    }

    // Every 5 s while a job runs, every 30 s otherwise (the list is a cheap read either way)
    pollJobs = () => {
        const active = (this.state.jobs?.jobs ?? []).some(isActive);
        if (!active && Date.now() - this.lastJobsPoll < 30000) return Promise.resolve(true);
        return this.loadJobs(false);
    }

    loadJobs = (first) => {
        this.lastJobsPoll = Date.now();
        return this.loc("Loading jobs", ["jobs"], { silent: true }).then(r => {
            if (typeof r === "string" || !r) return true;
            const wasActive = (this.state.jobs?.jobs ?? []).filter(isActive).map(j => j.id);
            this.setState({ jobs: r });
            // a job that just finished: refresh what the tabs show
            const nowActive = new Set((r.jobs ?? []).filter(isActive).map(j => j.id));
            if (!first && wasActive.some(id => !nowActive.has(id))) this.onDataChanged();
            return true;
        }).catch(() => true);
    }

    onDataChanged = () => { this.load(); this.tabRef?.reload?.(); }
    onJobsChanged = () => { this.lastJobsPoll = 0; this.loadJobs(false); }

    cancelJob = (id) => this.loc("Cancelling the job", ["translate", "cancel", id]).then(r => { if (typeof r === "string") enqueueSnackbar(r, { variant: "error" }); this.onJobsChanged(); }).catch(() => {});

    // What a version's clients download right now (patch, languages) and what changed in the branch's texts since its last
    // publish (that version's, or the branch's newest when no version is typed); read when the dialog opens / the version changes
    readPublished = () => {
        const { branch, publishVersion } = this.state;
        const v = publishVersion.trim();
        const valid = /^\d+\.\d+$/.test(v);
        if (valid) {
            this.loc("Reading the published texts", ["published", branch, v], { silent: true })
                .then(r => this.setState({ published: r }))
                .catch(() => this.setState({ published: null }));
        } else this.setState({ published: null });
        this.setState({ changesLoading: true });
        this.loc("Comparing with the last publish", valid ? ["changes", v] : ["changes"], { silent: true, timeout: 90000 })
            .then(r => this.setState({ changes: r }))
            .catch(() => this.setState({ changes: null }))
            .finally(() => this.setState({ changesLoading: false }));
    }

    publish = () => {
        const { branch: publishBranch, publishVersion } = this.state;
        this.setState({ publishing: true });
        this.loc(`Publishing ${publishBranch} ${publishVersion.trim()}`, ["publish", publishBranch, publishVersion.trim()], { timeout: 120000, retry: false })
            .then(r => {
                if (typeof r === "string") enqueueSnackbar(r, { variant: r.startsWith("Publish failed") || r.startsWith("Usage") ? "error" : "info" });
                else enqueueSnackbar(`Published ${publishBranch} ${publishVersion.trim()}: patch ${r?.patch ?? "?"}${r?.tags != null ? `, ${r.tags} tags` : ""}${r?.languages ? `, ${Array.isArray(r.languages) ? r.languages.length : r.languages} languages` : ""}`, { variant: "success" });
                this.setState({ publishOpen: false });
            })
            .catch(() => {})
            .finally(() => this.setState({ publishing: false }));
    }

    renderChanges() {
        const { changes, changesLoading } = this.state;
        if (changesLoading) return <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.75 }}>Comparing with the last publish…</Typography>;
        if (!changes) return null;
        if (typeof changes === "string") return <Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>{changes}</Typography>;
        if (!changes.published) return <Typography variant="caption" sx={{ display: "block", mt: 1.5 }}>{changes.message}</Typography>;
        const c = changes.counts ?? {};
        const list = [
            ...(changes.changed ?? []).map(x => ({ path: x.path, what: `changed: ${x.languages.join(", ")}` })),
            ...(changes.added ?? []).map(p => ({ path: p, what: "new" })),
            ...(changes.removed ?? []).map(p => ({ path: p, what: "removed" })),
        ];
        return (
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2">
                    {changes.identical ? `Nothing changed since ${changes.published} patch ${changes.patch}: the files would be the same.`
                        : `Since ${changes.published} patch ${changes.patch}: ${c.changed ?? 0} changed, ${c.added ?? 0} new, ${c.removed ?? 0} removed.`}
                </Typography>
                {list.length > 0 && (
                    <Box sx={{ maxHeight: 220, overflow: "auto", mt: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                        {list.slice(0, 300).map(x => <Typography key={x.path + x.what} variant="caption" sx={{ display: "block", fontFamily: "monospace" }}>{x.path} — {x.what}</Typography>)}
                    </Box>
                )}
            </Box>
        );
    }

    renderPublish() {
        const { publishOpen, branch, publishVersion, publishing, published } = this.state;
        const valid = /^\d+\.\d+$/.test(publishVersion.trim());
        return (
            <FormDialog open={publishOpen} title={`Publish the ${this.perBranch ? branch + " " : ""}texts to a client version`} onClose={() => this.setState({ publishOpen: false })}
                onConfirm={this.publish} confirmText={publishing ? "Publishing…" : `Publish ${branch} ${valid ? publishVersion.trim() : ""}`} confirmDisabled={!valid || publishing} cancelDisabled={publishing}>
                <Typography variant="body2" sx={{ mb: 2, maxWidth: 560 }}>
                    Writes every language's file of {this.perBranch ? <b>{branch}'s own texts</b> : "the texts"} for the {branch} branch and this major.minor to the CDN.
                    The clients of that version pick the new texts at their next boot. New or edited tags reach nobody until they are published.
                    {this.perBranch && " To publish another branch, select it at the top of the page first."}
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField size="small" label="Branch" value={branch} sx={{ width: 180 }} InputProps={{ readOnly: true }} />
                    <TextField size="small" label="Version (major.minor)" placeholder="0.23" value={publishVersion} sx={{ width: 180 }}
                        onChange={e => this.setState({ publishVersion: e.target.value, published: null })} onBlur={this.readPublished} />
                </Box>
                {published && typeof published === "object" && (
                    <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.75 }}>
                        Published now: patch {published.patch ?? "none"}{published.fallback ? " (an earlier version's texts)" : ""}{published.languages ? ` · ${Array.isArray(published.languages) ? published.languages.length : published.languages} languages` : ""}
                    </Typography>
                )}
                {this.renderChanges()}
            </FormDialog>
        );
    }

    // ---------- Copy to branch… (promote) ----------
    copyBody = (dryRun) => {
        const { copyMode, copyPaths } = this.state;
        const paths = copyPaths.split(/[\s,]+/).map(p => p.trim()).filter(Boolean);
        return copyMode === "changed" ? { changed: true, dryRun } : { paths, dryRun };
    }

    previewCopy = () => {
        const { branch, copyTo } = this.state;
        this.setState({ copyPreview: "loading" });
        loc("Comparing the branches", ["promote", branch, copyTo], { payload: this.copyBody(true), silent: true, timeout: 90000 })
            .then(r => this.setState({ copyPreview: r }))
            .catch(e => this.setState({ copyPreview: e.message ?? "failed" }));
    }

    copy = () => {
        const { branch, copyTo } = this.state;
        this.setState({ copying: true });
        loc(`Copying to ${copyTo}`, ["promote", branch, copyTo], { payload: this.copyBody(false), retry: false, timeout: 120000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                const n = (r.counts?.new ?? 0) + (r.counts?.changed ?? 0);
                enqueueSnackbar(`Copied ${n} tag${n === 1 ? "" : "s"} from ${branch} to ${copyTo}.`, { variant: "success" });
                this.setState({ copyOpen: false, copyPreview: null });
            })
            .catch(() => {})
            .finally(() => this.setState({ copying: false }));
    }

    renderCopy() {
        const { copyOpen, branch, copyTo, copyMode, copyPaths, copyPreview, copying } = this.state;
        const p = copyPreview && typeof copyPreview === "object" ? copyPreview : null;
        const n = p ? (p.counts?.new ?? 0) + (p.counts?.changed ?? 0) : 0;
        const columns = [
            { id: "path", label: "Tag", width: "34%", render: d => <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{d.path}</Typography> },
            { id: "kind", label: "", width: "10%", render: d => <Chip size="small" label={d.kind} color={d.kind === "new" ? "success" : d.kind === "changed" ? "warning" : "default"} /> },
            { id: "langs", label: "Languages", width: "14%", render: d => <Typography variant="caption">{d.languages?.length > 4 ? `${d.languages.length} languages` : (d.languages ?? []).join(", ")}{d.note ? " · note" : ""}</Typography> },
            { id: "diff", label: `Base text: ${copyTo} now → after`, width: "42%", render: d => (
                <Box>
                    {d.baseBefore != null && <Typography variant="caption" sx={{ display: "block", textDecoration: d.kind === "changed" ? "line-through" : "none", opacity: 0.7 }}>{d.baseBefore}</Typography>}
                    {d.kind !== "notInSource" && d.kind !== "same" && <Typography variant="caption" sx={{ display: "block" }}>{d.baseAfter}</Typography>}
                </Box>) },
        ];
        return (
            <FormDialog open={copyOpen} title={`Copy texts from ${branch} to another branch`} maxWidth="md" fullWidth
                onClose={() => this.setState({ copyOpen: false, copyPreview: null })} onConfirm={this.copy}
                confirmText={copying ? "Copying…" : p ? `Copy ${n} tag${n === 1 ? "" : "s"} to ${copyTo}` : "Preview first"} confirmDisabled={!p || n === 0 || copying} cancelDisabled={copying}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Copies tags with every language, tier, review state and hash from <b>{branch}</b> into the target branch's texts
                    (translate once on Development, copy to Internal with the build, to Beta / Production with the release). News texts
                    stay in their own branch; nothing is deleted in the target. Publish the target afterwards for its clients to see it.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <TextField select size="small" label="To" value={copyTo} sx={{ width: 160 }} onChange={e => this.setState({ copyTo: e.target.value, copyPreview: null })}>
                        {BRANCHES.filter(b => b !== branch).map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="Which tags" value={copyMode} sx={{ width: 240 }} onChange={e => this.setState({ copyMode: e.target.value, copyPreview: null })}>
                        <MenuItem value="changed">Every tag that differs</MenuItem>
                        <MenuItem value="paths">These tags…</MenuItem>
                    </TextField>
                    {copyMode === "paths" && <TextField size="small" label="Tag paths (one per line)" multiline minRows={2} maxRows={8} value={copyPaths} sx={{ flex: 1, minWidth: 260 }}
                        onChange={e => this.setState({ copyPaths: e.target.value, copyPreview: null })} />}
                    <Button variant="outlined" onClick={this.previewCopy} disabled={copyPreview === "loading" || (copyMode === "paths" && !copyPaths.trim())}>Preview</Button>
                </Box>
                {copyPreview === "loading" && <Typography variant="caption" sx={{ display: "block", mt: 2 }}>Comparing…</Typography>}
                {typeof copyPreview === "string" && copyPreview !== "loading" && <Typography variant="body2" color="error" sx={{ mt: 2 }}>{copyPreview}</Typography>}
                {p && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            {p.counts.new} new, {p.counts.changed} changed{p.counts.same ? `, ${p.counts.same} already the same` : ""}{p.counts.notInSource ? `, ${p.counts.notInSource} not in ${branch}` : ""}
                            {p.counts.owned ? ` · ${p.counts.owned} news text(s) skipped` : ""}{p.counts.onlyInTarget ? ` · ${p.counts.onlyInTarget} tag(s) only in ${copyTo} (kept)` : ""}
                        </Typography>
                        {p.items.length > 0 && <Box sx={{ maxHeight: 360, overflow: "auto" }}><DataTable columns={columns} rows={p.items} getRowKey={d => d.path} sx={FIXED_TABLE} /></Box>}
                        {p.more > 0 && <Typography variant="caption">… and {p.more} more</Typography>}
                    </Box>
                )}
            </FormDialog>
        );
    }

    setTab = (_, tab) => { this.setState({ tab, deepFolder: null, deepTag: null }); replaceDeep([tab]); }
    onFolderChange = (folder) => replaceDeep([this.state.tab, folder ?? ""]);

    render() {
        const { tab, tree, project, jobs, loading, deepFolder, deepTag, branch } = this.state;
        const canEdit = this.canEdit;
        const common = { loc: this.loc, tree, project, canEdit, branch, reloadTree: this.load, onJobsChanged: this.onJobsChanged, ref: r => { this.tabRef = r; } };
        const perBranch = this.perBranch;
        const running = (jobs?.jobs ?? []).filter(isActive).length;
        return (
            <PageContainer maxWidth={1600}>
                <SectionHeader title="Localization">
                    {(perBranch || tree == null) && (
                        <TextField select size="small" label="Branch" value={branch} sx={{ width: 170 }} onChange={e => this.setBranch(e.target.value)}>
                            {BRANCHES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                        </TextField>
                    )}
                    {tree && !perBranch && <Chip size="small" variant="outlined" label="one table for every branch" />}
                    {tree && <Chip size="small" label={`${tree.folders.reduce((n, f) => n + f.tags, 0)} tags · ${tree.languages.length} languages`} />}
                    {running > 0 && <Chip size="small" color="info" label={`${running} job${running > 1 ? "s" : ""} running`} onClick={() => this.setTab(null, "jobs")} />}
                    {!canEdit && <Chip size="small" label="read only" />}
                    {canEdit && <Button size="small" variant="outlined" onClick={() => this.setState({ publishOpen: true, changes: null }, this.readPublished)}>Publish{perBranch ? ` ${branch}` : ""} to a version…</Button>}
                    {canEdit && perBranch && <Button size="small" variant="outlined" onClick={() => this.setState({ copyOpen: true, copyPreview: null, copyTo: this.state.copyTo === branch ? (BRANCHES.find(b => b !== branch)) : this.state.copyTo })}>Copy to branch…</Button>}
                    <IconButton size="small" onClick={this.onDataChanged} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                </SectionHeader>
                {this.renderPublish()}
                {this.renderCopy()}
                <Tabs value={tab} onChange={this.setTab} sx={{ mb: 2 }}>
                    {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
                {tab === "jobs" && <JobPanel standalone jobs={jobs} canEdit={canEdit} onCancel={this.cancelJob} />}
                {tab !== "jobs" && (!tree || !project ? <Typography color="text.secondary">{loading ? "Loading…" : "The title has no texts yet."}</Typography> : (
                    <Box>
                        {tab === "strings" && <StringsTab key={`strings-${branch}`} {...common} initialFolder={deepFolder} initialTag={deepTag} onFolderChange={this.onFolderChange} />}
                        {tab === "coverage" && <CoverageTab key={`coverage-${branch}`} {...common} initialFolder={deepFolder} onFolderChange={this.onFolderChange} />}
                        {tab === "context" && <ContextTab key="context" {...common} onProjectChanged={this.load} />}
                        {tab === "import" && <ImportExportTab key={`import-${branch}`} {...common} onImported={this.onDataChanged} />}
                    </Box>
                ))}
            </PageContainer>
        );
    }
}
